// Mission reports (Ordre 15): labels and pure rules shared by the mission page, the report page and the missions list.
// The server decides every right; these helpers only word what it answered.
import { errorMessage, isVersionConflict } from "./api";

export const REPORT_STATUS = {
  draft: { label: "Compte rendu en cours", short: "En cours", tone: "bg-slate-100 text-slate-700" },
  submitted: { label: "À valider", short: "À valider", tone: "bg-amber-50 text-amber-800" },
  returned: { label: "À corriger", short: "À corriger", tone: "bg-red-50 text-red-800" },
  approved: { label: "Validée", short: "Validée", tone: "bg-emerald-50 text-emerald-800" },
  published: { label: "Compte rendu publié", short: "Publié", tone: "bg-emerald-50 text-emerald-800" },
};

export const ENDED_STATUSES = ["completed", "interrupted"];

export const DECISION_LABELS = { approved: "Validé", returned: "Renvoyé pour correction", published: "Publié", reopened: "Rouvert" };

// What the mission's badge says once it has ended: the report carries the validation state, the mission keeps « Terminée ».
export function reportState(status) {
  return status ? REPORT_STATUS[status] || null : null;
}

// The single next step shown on the mission page, from the server's capabilities.
export function nextStep(data) {
  if (!data) return null;
  const caps = data.capabilities || {};
  const report = data.report;
  if (!report) return caps.start ? { key: "start", label: "Rédiger le compte rendu" } : null;
  if (caps.review) return { key: "review", label: "Relire et valider" };
  if (caps.write) return { key: "write", label: report.status === "returned" ? "Corriger le compte rendu" : "Continuer le compte rendu" };
  return { key: "read", label: "Voir le compte rendu" };
}

// Who must act now, in words.
export function waitingFor(data) {
  const report = data?.report;
  if (!report) return ENDED_STATUSES.includes(data?.mission?.status) ? "L'équipe doit rédiger le compte rendu." : "Le compte rendu se rédige une fois la mission terminée.";
  if (report.status === "draft") return "En cours de rédaction par l'équipe.";
  if (report.status === "returned") return "Renvoyé pour correction : l'équipe doit le reprendre.";
  if (report.status === "submitted") return data.capabilities?.review_blocked ? "Vous l'avez rédigé ou soumis : un autre responsable doit le valider." : "En attente de validation par un responsable.";
  return null;
}

export function contributionKey(member) {
  return `${member.kind}:${member.id}`;
}

// The contribution of `member` as the server returned it, or an empty one (version 0).
export function contributionOf(report, member) {
  const key = contributionKey(member);
  return (report?.contributions || []).find((c) => contributionKey(c.member) === key) || { member, text: "", version: 0 };
}

export function answerBody(answer, changes = {}) {
  return { expected_version: answer?.version || 0, value: answer?.value ?? null, file_ids: (answer?.files || []).map((f) => f.id), ...changes };
}

export function formatMinutes(total) {
  if (!total) return "0 min";
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h} h${m ? ` ${String(m).padStart(2, "0")}` : ""}` : `${m} min`;
}

// The facts of the mission (team, checklists, time, equipment, training) as short lines.
export function factLines(facts) {
  if (!facts) return [];
  const lines = [];
  if (facts.team?.length) lines.push(`Équipe : ${facts.team.map((t) => t.name).join(", ")}`);
  if (facts.checklists?.length) {
    const done = facts.checklists.filter((c) => c.status === "completed").length;
    const anomalies = facts.checklists.reduce((n, c) => n + (c.anomalies || 0), 0);
    lines.push(`Procédures : ${done} terminée${done > 1 ? "s" : ""} sur ${facts.checklists.length}${anomalies ? ` · ${anomalies} anomalie${anomalies > 1 ? "s" : ""}` : ""}`);
  }
  if (facts.time) {
    const total = facts.time.totals?.total || 0;
    lines.push(`Temps pointés : ${formatMinutes(total)}${facts.time.validated ? " (validés)" : ""}`);
  }
  if (typeof facts.equipment_issues === "number") lines.push(`Anomalies de matériel ouvertes : ${facts.equipment_issues}`);
  if (typeof facts.training_sessions === "number") lines.push(`Sessions de formation : ${facts.training_sessions}`);
  return lines;
}

export function reportErrorMessage(error, fallback = "Une erreur est survenue. Réessayez.") {
  if (!error?.response) return "Connexion impossible. Vérifiez votre réseau : votre texte n'a pas été enregistré.";
  if (isVersionConflict(error)) return "Un collègue vient de modifier ce passage : son texte est affiché, vérifiez puis recommencez.";
  return errorMessage(error, fallback);
}
